import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import { AppShell } from '@/components/AppShell';
import { BrandMarkIcon, SparkIcon } from '@/components/Icons';
import { PwaInstallPrompt } from '@/components/PwaInstallPrompt';
import { useLaunchState } from '@/context/LaunchContext';
import { useSessionState } from '@/context/SessionContext';
import { useThemeState } from '@/context/ThemeContext';
import { AnalysisPage } from '@/pages/AnalysisPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { CalendarPage } from '@/pages/CalendarPage';
import { ContentManagementPage } from '@/pages/ContentManagementPage';
import { FeedbackPage } from '@/pages/FeedbackPage';
import { LoginPage } from '@/pages/LoginPage';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { StaffDetailPage } from '@/pages/StaffDetailPage';
import { StaffPage } from '@/pages/StaffPage';
import { ServicesPage } from '@/pages/ServicesPage';
import { AttendancePage } from '@/pages/AttendancePage';
import { ClientsPage } from '@/pages/ClientsPage';
import { VisitorPage } from '@/pages/VisitorPage';
import { WelcomePage } from '@/pages/WelcomePage';

function LoadingScreen() {
  return (
    <div className="app-root loading-screen">
      <div className="ambient-glow one" />
      <div className="ambient-glow two" />
      <div className="ambient-glow three" />
      <div className="loading-screen-shell">
        <div className="loading-screen-copy">
          <div className="brand-mark loading-screen-brand" aria-hidden="true">
            <BrandMarkIcon />
          </div>
          <div className="loading-screen-copy-block">
            <p className="eyebrow">SlotLooks PWA</p>
            <h1 className="display-title loading-screen-title">
              <span>Preparing</span>
              <span>workspace</span>
            </h1>
            <p className="copy loading-screen-text">
              Syncing theme, session, and role routing before the dashboard appears.
            </p>
          </div>
        </div>

        <div className="loading-screen-status">
          <span className="badge">
            <SparkIcon size={14} />
            Loading
          </span>
          <span className="badge soft">Theme, auth, and routes</span>
        </div>
      </div>
    </div>
  );
}

function ProtectedLayout() {
  const { welcomeSeen } = useLaunchState();
  const { isReady: sessionReady, isAuthenticated, sessionRole } = useSessionState();
  const { isReady: themeReady } = useThemeState();
  const location = useLocation();

  if (welcomeSeen === null || !sessionReady || !themeReady) {
    return <LoadingScreen />;
  }

  if (!welcomeSeen) {
    return <Navigate to="/" replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (sessionRole === 'staff') {
    const adminOnlyPrefixes = [
      '/dashboard',
      '/explore',
      '/analysis',
      '/calendar',
      '/content-management',
      '/services',
      '/staff',
    ];

    if (adminOnlyPrefixes.some((prefix) => location.pathname.startsWith(prefix))) {
      return <Navigate to="/attendance" replace />;
    }
  }

  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}

function PublicLayout() {
  const { welcomeSeen } = useLaunchState();
  const { isReady: sessionReady, isAuthenticated, sessionRole } = useSessionState();
  const { isReady: themeReady } = useThemeState();
  const location = useLocation();
  const isWelcomeRoute = location.pathname === '/' || location.pathname === '/welcome';

  if (welcomeSeen === null || !sessionReady || !themeReady) {
    return <LoadingScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to={sessionRole === 'staff' ? '/attendance' : '/dashboard'} replace />;
  }

  if (location.pathname === '/login' && !welcomeSeen) {
    return <Navigate to="/" replace />;
  }

  if (isWelcomeRoute && welcomeSeen) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}

export default function App() {
  return (
    <>
      <Routes>
        <Route element={<PublicLayout />}>
          <Route path="/" element={<WelcomePage />} />
          <Route path="/welcome" element={<Navigate to="/" replace />} />
          <Route path="/login" element={<LoginPage />} />
        </Route>

        <Route element={<ProtectedLayout />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/explore" element={<DashboardPage />} />
          <Route path="/analysis" element={<AnalysisPage />} />
          <Route path="/calendar" element={<CalendarPage />} />
          <Route path="/visitor" element={<VisitorPage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/clients/:clientKey" element={<ClientsPage />} />
          <Route path="/attendance" element={<AttendancePage />} />
          <Route path="/content-management" element={<ContentManagementPage />} />
          <Route path="/content-management/services" element={<ServicesPage />} />
          <Route path="/content-management/staff" element={<StaffPage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/staff" element={<StaffPage />} />
          <Route path="/staff/:staffId" element={<StaffDetailPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
        <Route path="/feedback/:token" element={<FeedbackPage />} />
      </Routes>
      <PwaInstallPrompt />
    </>
  );
}
